import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Globe,
  Search,
  Loader2,
  Circle,
  Clock,
  CheckCircle2,
  XCircle,
  MapPin,
  Eye,
  Utensils,
  CalendarClock,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import Button from "@/components/Button";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ProfileStatus = "draft" | "pending_review" | "published" | "rejected";

interface PublicProfileListItem {
  id: string;
  status: ProfileStatus;
  submittedAt: string | null;
  reviewedAt: string | null;
  publishedAt: string | null;
  location: {
    id: string;
    name: string;
    slug: string;
    city: string;
    phone: string | null;
    logo: string | null;
    coverImage: string | null;
  };
  organization: {
    name: string;
    slug: string;
    bookingMode: "tables" | "appointments";
  };
  images: { id: string; url: string; alt: string | null; sortOrder: number }[];
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface Counts {
  draft: number;
  pending_review: number;
  published: number;
  rejected: number;
}

const STATUS_CONFIG: Record<
  ProfileStatus,
  { label: string; badgeClass: string; icon: typeof Circle }
> = {
  draft: { label: "Borrador", badgeClass: "bg-gray-100 text-gray-700", icon: Circle },
  pending_review: {
    label: "Pendiente",
    badgeClass: "bg-yellow-100 text-yellow-800",
    icon: Clock,
  },
  published: {
    label: "Publicada",
    badgeClass: "bg-green-100 text-green-800",
    icon: CheckCircle2,
  },
  rejected: {
    label: "Rechazada",
    badgeClass: "bg-red-100 text-red-800",
    icon: XCircle,
  },
};

const FILTERS: { value: "all" | ProfileStatus; label: string }[] = [
  { value: "pending_review", label: "Pendientes" },
  { value: "published", label: "Publicadas" },
  { value: "draft", label: "Borradores" },
  { value: "rejected", label: "Rechazadas" },
  { value: "all", label: "Todas" },
];

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function StatusBadge({ status }: { status: ProfileStatus }) {
  const cfg = STATUS_CONFIG[status];
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badgeClass}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminPublicProfiles() {
  const [items, setItems] = useState<PublicProfileListItem[]>([]);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [statusFilter, setStatusFilter] = useState<"all" | ProfileStatus>(
    "pending_review",
  );
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await apiClient.get("/admin/public-profiles/counts");
      setCounts(res.data.data);
    } catch {
      // silencioso: los contadores son solo un adorno visual
    }
  }, []);

  const fetchList = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: page.toString(),
          limit: pagination.limit.toString(),
        });
        if (statusFilter !== "all") params.append("status", statusFilter);
        if (search) params.append("search", search);

        const res = await apiClient.get(`/admin/public-profiles?${params}`);
        setItems(res.data.data);
        setPagination(res.data.pagination);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, search, pagination.limit],
  );

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    const t = setTimeout(() => fetchList(1), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Globe className="w-6 h-6 text-primary-600" />
          Páginas web
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Revisa y aprueba las páginas públicas que solicitan los clientes.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => {
          const count = f.value !== "all" ? counts?.[f.value] : undefined;
          return (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                statusFilter === f.value
                  ? "bg-primary-600 text-white border-primary-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {f.label}
              {typeof count === "number" && count > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${
                    statusFilter === f.value
                      ? "bg-white/25 text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex items-center gap-2">
        <Search className="w-4 h-4 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por negocio, ciudad u organización..."
          className="flex-1 outline-none text-sm"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            No hay páginas web
          </h3>
          <p className="text-gray-500 text-sm">
            No hay solicitudes con este filtro.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {items.map((item) => (
              <Link
                key={item.id}
                to={`/admin/public-profiles/${item.id}`}
                className="block bg-white rounded-xl border border-gray-200 p-4 hover:border-primary-300 transition-colors"
              >
                <div className="flex items-start gap-3">
                  {item.location.logo ? (
                    <img
                      src={item.location.logo}
                      alt=""
                      className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-5 h-5 text-primary-600" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-gray-900 truncate">
                        {item.location.name}
                      </p>
                      <StatusBadge status={item.status} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {item.location.city} · {item.organization.name}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Solicitado: {formatDate(item.submittedAt)}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left">
                  <th className="px-4 py-3 font-medium text-gray-700">
                    Negocio
                  </th>
                  <th className="px-3 py-3 font-medium text-gray-700">
                    Ciudad
                  </th>
                  <th className="px-3 py-3 font-medium text-gray-700">
                    Modalidad
                  </th>
                  <th className="px-3 py-3 font-medium text-gray-700">
                    Solicitado
                  </th>
                  <th className="px-3 py-3 font-medium text-gray-700">
                    Estado
                  </th>
                  <th className="px-3 py-3 font-medium text-gray-700"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {item.location.logo ? (
                          <img
                            src={item.location.logo}
                            alt=""
                            className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <Globe className="w-4 h-4 text-primary-600" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {item.location.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {item.organization.name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-700">
                      {item.location.city}
                    </td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                        {item.organization.bookingMode === "appointments" ? (
                          <CalendarClock className="w-3.5 h-3.5" />
                        ) : (
                          <Utensils className="w-3.5 h-3.5" />
                        )}
                        {item.organization.bookingMode === "appointments"
                          ? "Citas"
                          : "Mesas"}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-gray-500">
                      {formatDate(item.submittedAt)}
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-3 py-3">
                      <Link
                        to={`/admin/public-profiles/${item.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Revisar
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-xs text-gray-500">
                Mostrando {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                de {pagination.total}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => fetchList(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => fetchList(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
